import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useEffect, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Package, Plus, Edit, Trash2, Loader2, AlertCircle } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { DealLineItemRecord } from "@/types";

const GST_RATES = [0, 5, 12, 18, 28];
const CUSTOM_LINE = "__custom__";

const EMPTY_FORM = {
  variant_id: "",
  product_id: "",
  product_name: "",
  unit: "NOS",
  quantity: "1",
  unit_price: "",
  discount_pct: "0",
  gst_rate: 18,
};

const selectClass =
  "h-9 sm:h-10 w-full rounded-md border border-border bg-background px-3 sm:px-3.5 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

interface DealProductsCardProps {
  dealId: string;
  /** The customer, whose price list prices the products */
  accountId?: string;
  isClosed: boolean;
  /**
   * Called with the current line count after the initial load (amountChanged = false)
   * and after every add/edit/remove (amountChanged = true) so the parent can reload the deal.
   */
  onChanged: (lineCount: number, amountChanged: boolean) => void;
}

export function DealProductsCard({ dealId, accountId, isClosed, onChanged }: DealProductsCardProps) {
  // The price last filled in from the customer's price list; while the price still equals it, a new quantity re-prices
  const [autoPrice, setAutoPrice] = useState<string | null>(null);
  const [variants, setVariants] = useState<{ id: string; code: string; label: string; price_delta: number | string; status: boolean }[]>([]);
  const [lines, setLines] = useState<DealLineItemRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // null = closed, "new" = add, record = edit
  const [dialogLine, setDialogLine] = useState<DealLineItemRecord | "new" | null>(null);
  const [deleteLine, setDeleteLine] = useState<DealLineItemRecord | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { success, error } = useToast();
  const { hasPermission, user } = useAuth();
  const userLimit = Number(user?.discount_limit_pct || 0);
  const canEdit = hasPermission("deal.update") && !isClosed;
  const canReadProducts = hasPermission("product.read");

  const loadLines = async () => {
    try {
      const res = await api.get(`/api/DealLineItem/ListByDeal/${dealId}`);
      const list = unwrapArray<DealLineItemRecord>(res);
      setLines(list);
      return list;
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load deal products");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLines().then((list) => list && onChanged(list.length, false));
  }, [dealId]);

  const subtotal = lines.reduce((sum, l) => sum + Number(l.line_total || 0), 0);
  const gstTotal = lines.reduce(
    (sum, l) => sum + (Number(l.line_total || 0) * Number(l.gst_rate || 0)) / 100,
    0
  );

  const previewTotal = (() => {
    const qty = Number(form.quantity) || 0;
    const price = Number(form.unit_price) || 0;
    const disc = Number(form.discount_pct) || 0;
    return qty * price * (1 - disc / 100);
  })();

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setVariants([]);
    setDialogLine("new");
  };

  const openEdit = (l: DealLineItemRecord) => {
    setVariants([]);
    setForm({
      variant_id: "",
      product_id: l.product_id || CUSTOM_LINE,
      product_name: l.product_name,
      unit: l.unit,
      quantity: String(Number(l.quantity)),
      unit_price: String(Number(l.unit_price)),
      discount_pct: String(Number(l.discount_pct)),
      gst_rate: Number(l.gst_rate),
    });
    setDialogLine(l);
  };

  // Picking a catalog product pre-fills its price, unit and GST
  const handleProductPick = (productId: string, row?: any) => {
    if (!row || productId === CUSTOM_LINE) {
      setVariants([]);
      setForm({ ...form, variant_id: "", product_id: productId, product_name: productId === CUSTOM_LINE ? form.product_name : "" });
      return;
    }
    setForm({
      ...form,
      variant_id: "",
      product_id: row.id,
      product_name: row.name,
      unit: row.unit,
      unit_price: String(Number(row.unit_price)),
      gst_rate: Number(row.gst_rate),
    });
    setAutoPrice(null);
    setVariants([]);
    api.get(`/api/ProductOption/Variants/${row.id}`).then((r) => setVariants(unwrapArray<any>(r).filter((v) => v.status))).catch(() => undefined);
    // The catalog price shows first; the customer's listed price replaces it a moment later
    listedPrice(row.id, Number(form.quantity) || 1).then((p) => {
      if (p !== null) {
        setForm((f) => (f.product_id === row.id ? { ...f, unit_price: String(p) } : f));
        setAutoPrice(String(p));
      }
    });
  };

  // Choosing a variant sells at the listed price plus its difference
  const pickVariant = async (variantId: string) => {
    const v = variants.find((x) => x.id === variantId);
    const base = await listedPrice(form.product_id, Number(form.quantity) || 1);
    setForm((f) => ({ ...f, variant_id: variantId, ...(base !== null ? { unit_price: String(Math.round((base + (v ? Number(v.price_delta) : 0)) * 100) / 100) } : {}) }));
    setAutoPrice(null);
  };

  const listedPrice = async (productId: string, qty: number) => {
    try {
      const q = new URLSearchParams({ product_id: productId, qty: String(qty > 0 ? qty : 1) });
      if (accountId) q.set("account_id", accountId);
      return Number(unwrapObject<{ unit_price: number }>(await api.get(`/api/PriceList/Price?${q}`)).unit_price);
    } catch {
      return null;
    }
  };

  // A new quantity can reach another price slab, unless the price was typed by hand
  const changeQuantity = async (value: string) => {
    setForm((f) => ({ ...f, quantity: value }));
    if (!form.product_id || form.product_id === CUSTOM_LINE || autoPrice === null || Number(form.unit_price) !== Number(autoPrice)) return;
    const p = await listedPrice(form.product_id, Number(value));
    if (p !== null) {
      setForm((f) => ({ ...f, unit_price: String(p) }));
      setAutoPrice(String(p));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isCustom = form.product_id === CUSTOM_LINE || !form.product_id;
    if (dialogLine === "new" && !form.product_id) {
      error("Validation Error", "Select a product or choose a custom line");
      return;
    }
    if (isCustom && !form.product_name.trim()) {
      error("Validation Error", "Enter a description for the custom line");
      return;
    }
    if (!(Number(form.quantity) > 0)) {
      error("Validation Error", "Quantity must be greater than zero");
      return;
    }
    const discount = Number(form.discount_pct) || 0;
    if (discount < 0 || discount > 100) {
      error("Validation Error", "Discount must be between 0 and 100");
      return;
    }

    const payload = {
      deal_id: dealId,
      product_id: isCustom ? undefined : form.product_id,
      variant_id: !isCustom && form.variant_id && dialogLine === "new" ? form.variant_id : undefined,
      product_name: form.product_name.trim() || undefined,
      unit: form.unit,
      quantity: Number(form.quantity),
      unit_price: form.unit_price === "" ? undefined : Number(form.unit_price),
      discount_pct: discount,
      gst_rate: Number(form.gst_rate),
    };

    setActionLoading(true);
    try {
      if (dialogLine === "new") {
        await api.post("/api/DealLineItem/Insert", payload);
        success("Product Added", "Deal amount has been recalculated");
      } else if (dialogLine) {
        await api.put(`/api/DealLineItem/Update/${dialogLine.id}`, payload);
        success("Line Updated", "Deal amount has been recalculated");
      }
      setDialogLine(null);
      const list = await loadLines();
      onChanged(list ? list.length : lines.length, true);
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to save product line");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteLine) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/DealLineItem/Delete/${deleteLine.id}`);
      success("Line Removed", "Product removed from deal");
      setDeleteLine(null);
      const list = await loadLines();
      onChanged(list ? list.length : lines.length, true);
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to remove product line");
    } finally {
      setActionLoading(false);
    }
  };

  const isCustomForm = form.product_id === CUSTOM_LINE;

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Package className="size-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">Products</h3>
          {lines.length > 0 && (
            <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground">
              {lines.length}
            </span>
          )}
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={openAdd} className="h-8 gap-1.5 text-xs">
            <Plus className="size-3.5" />
            Add Product
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : lines.length === 0 ? (
        <div className="py-6 px-4 text-center text-xs text-muted-foreground">
          No products on this deal yet.
          {canEdit && " Add products to calculate the deal amount automatically."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Product</TableHead>
                  <TableHead className="text-xs text-right">Qty</TableHead>
                  <TableHead className="text-xs text-right">Price</TableHead>
                  <TableHead className="text-xs text-right">Disc.</TableHead>
                  <TableHead className="text-xs text-right">GST</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                  {canEdit && <TableHead className="w-16" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-xs">
                      <div className="font-medium text-foreground">{l.product_name}</div>
                      {l.variant_label && <div className="text-xs">{l.variant_label}</div>}
                      {l.product_code && (
                        <code className="text-[10px] text-muted-foreground">{l.product_code}</code>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-right whitespace-nowrap">
                      {Number(l.quantity)} {l.unit}
                    </TableCell>
                    <TableCell className="text-xs text-right">{formatINR(l.unit_price)}</TableCell>
                    <TableCell className="text-xs text-right">
                      {Number(l.discount_pct) > 0 ? (
                        <div className="flex items-center justify-end gap-1">
                          <span>{Number(l.discount_pct)}%</span>
                          {Number(l.discount_pct) > userLimit && (
                            <span
                              title={`Above your ${userLimit}% limit: will require approval when quoted`}
                              className="inline-flex items-center text-warning"
                            >
                              <AlertCircle className="size-3" />
                            </span>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-right">{Number(l.gst_rate)}%</TableCell>
                    <TableCell className="text-xs text-right font-semibold">
                      {formatINR(l.line_total)}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" className="size-7" onClick={() => openEdit(l)} title="Edit line">
                          <Edit className="size-3.5 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="icon" className="size-7" onClick={() => setDeleteLine(l)} title="Remove line">
                          <Trash2 className="size-3.5 text-danger" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div className="border-t px-4 py-3 text-xs space-y-1 ml-auto max-w-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal (deal amount)</span>
              <span className="font-semibold">{formatINR(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">GST</span>
              <span>{formatINR(gstTotal)}</span>
            </div>
            <div className="flex justify-between border-t pt-1 text-sm">
              <span className="font-semibold">Total incl. GST</span>
              <span className="font-bold">{formatINR(subtotal + gstTotal)}</span>
            </div>
          </div>
        </>
      )}

      {/* Dialog: Add / Edit line */}
      <Dialog open={!!dialogLine} onOpenChange={(open) => !open && setDialogLine(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="size-5 text-primary" />
              {dialogLine === "new" ? "Add Product to Deal" : "Edit Product Line"}
            </DialogTitle>
            <DialogDescription>Prices exclude GST. The deal amount updates automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3.5 py-2 text-xs sm:text-sm">
            {dialogLine === "new" && (
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Product *</Label>
                {canReadProducts ? (
                  <SearchSelect
                    endpoint="/api/Product/List"
                    extraParams={{ active: "true" }}
                    value={form.product_id}
                    selectedLabel={form.product_id === CUSTOM_LINE ? form.product_name || "+ Custom line" : form.product_name}
                    getLabel={(row) => `${row.name} (${row.code}) · ${formatINR(row.unit_price)}/${row.unit}`}
                    placeholder="Search products"
                    allowEmpty
                    emptyLabel="-- Select a product --"
                    extraOptions={[{ value: CUSTOM_LINE, label: "+ Custom line (not in catalog)" }]}
                    onChange={(id, row) => handleProductPick(id, row)}
                  />
                ) : (
                  <SearchableSelect value={form.product_id} onChange={(e) => handleProductPick(e.target.value)} className={selectClass}>
                    <option value="">-- Select a product --</option>
                    <option value={CUSTOM_LINE}>+ Custom line (not in catalog)</option>
                  </SearchableSelect>
                )}
                {!canReadProducts && (
                  <p className="text-[11px] text-muted-foreground">
                    You don't have access to the product catalog; only custom lines are available.
                  </p>
                )}
              </div>
            )}

            {dialogLine === "new" && variants.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Variant</Label>
                <select value={form.variant_id} onChange={(e) => pickVariant(e.target.value)} className={selectClass}>
                  <option value="">-- Product as is --</option>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}{Number(v.price_delta) ? ` (${Number(v.price_delta) > 0 ? "+" : "-"}${formatINR(Math.abs(Number(v.price_delta)))})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(isCustomForm || dialogLine !== "new") && (
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Description {isCustomForm && "*"}</Label>
                <Input
                  value={form.product_name}
                  onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  placeholder="Installation charges"
                />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Quantity *</Label>
                <Input
                  type="number"
                  min={0.01}
                  step="0.01"
                  required
                  value={form.quantity}
                  onChange={(e) => changeQuantity(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Unit Price (₹) *</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  required
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Unit</Label>
                <Input
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value.toUpperCase() })}
                  className="uppercase"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Discount %</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.discount_pct}
                  onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                />
                {Number(form.discount_pct || 0) > userLimit && (
                  <div className="flex items-center gap-1.5 text-[11px] text-warning bg-warning/10 border border-warning/20 px-2 py-1 rounded-md mt-1">
                    <AlertCircle className="size-3.5 shrink-0" />
                    <span>Above your {userLimit}% limit: will require approval when quoted</span>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">GST Rate</Label>
                <SearchableSelect
                  value={form.gst_rate}
                  onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })}
                  className={selectClass}
                >
                  {GST_RATES.map((r) => (
                    <option key={r} value={r}>
                      {r}%
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>

            <div className="rounded-lg bg-muted/40 px-3.5 py-2.5 text-xs sm:text-sm flex justify-between">
              <span className="text-muted-foreground">Line total (before GST)</span>
              <span className="font-semibold text-foreground">{formatINR(previewTotal)}</span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogLine(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete line */}
      <Dialog open={!!deleteLine} onOpenChange={(open) => !open && setDeleteLine(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-danger">Remove Product</DialogTitle>
            <DialogDescription>
              Remove <strong className="text-foreground">{deleteLine?.product_name}</strong> from this
              deal? The deal amount will be recalculated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteLine(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
